import { localizeError } from '../i18n/format.ts';
import { tr } from '../i18n/i18n.ts';
import { ApiError, apiAt } from '../api.ts';
import { isNative } from '../platform.ts';

/**
 * Enda auth-adaptern i klienten – alla ingångar (sidomeny, mobilens kontoåtgärd, Inställningar)
 * använder den. Den anropar better-auths befintliga endpoints; ingen egen lösenords- eller
 * tokenhantering. Lösenord passerar bara i anropets kropp och lagras aldrig.
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

export interface ServerConfig {
  app: 'autoday';
  publicOrigin: string;
  password: { minLength: number; maxLength: number };
  session: { rememberedDays: number; refreshAfterHours: number; notRememberedHours: number };
  features: {
    passwordReset: boolean;
    /** 'local-outbox' = serverns testtransport: inget mejl skickas på riktigt. Saknas på äldre servrar. */
    passwordResetDelivery?: 'email' | 'local-outbox' | null;
    emailVerification: boolean;
    /** 'private' = bara inbjudna adresser kan skapa konto. Saknas på äldre servrar (= öppen). */
    signup?: 'open' | 'private';
    /** Kan servern skicka fjärrpush (FCM/APNs) till native-appen? Saknas på äldre servrar (= nej). */
    nativePush?: { fcm: boolean; apns: boolean };
  };
  passwordReset?: { linkMinutes: number };
}

export async function fetchServerConfig(base: string): Promise<ServerConfig> {
  const { data } = await apiAt<ServerConfig>({ base }, '/api/config', { timeoutMs: 8000 });
  if (!data || data.app !== 'autoday') throw new ApiError(200, 'NOT_AUTODAY', tr('Ingen AutoDay-server svarade på adressen.', 'No AutoDay server responded at the address.'));
  return data;
}

interface SignResult {
  user: AuthUser;
  /** Native: bärartoken. Webb: null (httpOnly-cookie). */
  token: string | null;
}

function toUser(u: { id?: string; email?: string; name?: string } | undefined): AuthUser {
  if (!u?.id || !u.email) throw new ApiError(200, 'BAD_RESPONSE', tr('Servern gav ett oväntat svar.', 'The server gave an unexpected response.'));
  return { id: u.id, email: u.email, name: u.name ?? '' };
}

export async function signIn(base: string, input: { email: string; password: string; rememberMe: boolean }): Promise<SignResult> {
  const { data, headers } = await apiAt<{ user?: AuthUser; token?: string }>({ base }, '/api/auth/sign-in/email', {
    body: { email: input.email.trim(), password: input.password, rememberMe: input.rememberMe },
  });
  return { user: toUser(data?.user), token: isNative() ? headers.get('set-auth-token') ?? data?.token ?? null : null };
}

export async function signUp(base: string, input: { name: string; email: string; password: string; rememberMe: boolean }): Promise<SignResult> {
  const { data, headers } = await apiAt<{ user?: AuthUser; token?: string }>({ base }, '/api/auth/sign-up/email', {
    body: { name: input.name.trim(), email: input.email.trim(), password: input.password, rememberMe: input.rememberMe },
  });
  return { user: toUser(data?.user), token: isNative() ? headers.get('set-auth-token') ?? data?.token ?? null : null };
}

/**
 * Verifierar sessionen mot servern. null = servern säger att det inte finns någon giltig session.
 * Nätverksfel kastas som ApiError NETWORK/TIMEOUT – ett nätverksfel är INTE en utgången session.
 */
export async function getSession(base: string, token?: string | null): Promise<AuthUser | null> {
  const { data } = await apiAt<{ user?: AuthUser } | null>({ base, token: token ?? null }, '/api/auth/get-session', { timeoutMs: 10000 });
  if (!data?.user) return null;
  return toUser(data.user);
}

/** Ber servern mejla en återställningslänk. Svaret är detsamma oavsett om kontot finns. */
export async function requestPasswordReset(base: string, email: string): Promise<void> {
  await apiAt({ base }, '/api/auth/request-password-reset', { body: { email: email.trim() } });
}

/** Sätter nytt lösenord med engångstoken från återställningslänken. Servern loggar ut alla enheter. */
export async function resetPassword(base: string, token: string, newPassword: string): Promise<void> {
  await apiAt({ base }, '/api/auth/reset-password', { body: { token, newPassword } });
}

export async function signOut(base: string, token?: string | null): Promise<void> {
  await apiAt({ base, token: token ?? null }, '/api/auth/sign-out', { body: {}, timeoutMs: 10000 });
}

export function isNetworkError(e: unknown): boolean {
  return e instanceof ApiError && (e.code === 'NETWORK' || e.code === 'TIMEOUT');
}

/** Svenska, icke-avslöjande felmeddelanden. */
export function authErrorMessage(e: unknown, mode: 'login' | 'register' | 'forgot' | 'reset', policy?: ServerConfig['password']): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? localizeError(e.message) : tr('Något gick fel. Försök igen.', 'Something went wrong. Try again.');
  if (isNetworkError(e)) return tr('Kunde inte nå AutoDay-servern. Kontrollera anslutningen och försök igen. Du kan fortsätta använda kalendern lokalt.', 'Could not reach the AutoDay server. Check the connection and try again. You can keep using the calendar locally.');
  if (e.code === 'NOT_AUTODAY' || e.code === 'BAD_RESPONSE') return tr('Ingen AutoDay-server svarade på adressen. Kontrollera serverinställningen.', 'No AutoDay server responded at the address. Check the server setting.');
  if (e.status === 429) return tr(`För många försök. Vänta${e.retryAfter ? ` ${e.retryAfter} sekunder` : ' en stund'} och försök igen.`, `Too many attempts. Wait${e.retryAfter ? ` ${e.retryAfter} seconds` : ' a while'} and try again.`);
  if (e.code === 'INVALID_ORIGIN' || e.code === 'MISSING_OR_NULL_ORIGIN') return tr('Servern godkänner inte inloggning från den här webbadressen. Öppna AutoDay via den adress som servern är inställd för.', 'The server does not accept sign-in from this web address. Open AutoDay at the address the server is set up for.');
  switch (e.code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
      return tr('Fel e-postadress eller lösenord.', 'Wrong email address or password.');
    case 'INVALID_EMAIL':
      return 'Ange en giltig e-postadress.';
    case 'PASSWORD_TOO_SHORT':
      return tr(`Lösenordet måste ha minst ${policy?.minLength ?? 10} tecken.`, `The password must have at least ${policy?.minLength ?? 10} characters.`);
    case 'PASSWORD_TOO_LONG':
      return tr(`Lösenordet får ha högst ${policy?.maxLength ?? 256} tecken.`, `The password can have at most ${policy?.maxLength ?? 256} characters.`);
    case 'SIGNUP_DISABLED':
      return tr('Den här servern är privat. Nya konton kan bara skapas för inbjudna e-postadresser.', 'This server is private. New accounts can only be created for invited email addresses.');
    case 'RESET_PASSWORD_DISABLED':
      return tr('Lösenordsåterställning via e-post är inte aktiverad på den här servern.', 'Password reset by email is not enabled on this server.');
    case 'INVALID_TOKEN':
      return tr('Länken är ogiltig, redan använd eller för gammal. Begär en ny länk via Logga in → Glömt lösenordet.', 'The link is invalid, already used or too old. Request a new link via Sign in → Forgot password.');
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return tr('Det gick inte att skapa ett konto med den e-postadressen. Har du redan ett konto? Logga in i stället.', 'An account could not be created with that email address. Do you already have an account? Sign in instead.');
  }
  if (e.status >= 500) return tr('Serverfel. Försök igen om en stund.', 'Server error. Try again in a while.');
  if (mode === 'forgot' || mode === 'reset') return tr('Det gick inte att byta lösenord just nu. Försök igen.', 'The password could not be changed right now. Try again.');
  return mode === 'login' ? tr('Inloggningen misslyckades. Försök igen.', 'Sign-in failed. Try again.') : tr('Kontot kunde inte skapas. Försök igen.', 'The account could not be created. Try again.');
}
